/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { ContentCopy, Delete } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Event } from '@/api/bol';
import { createTraceDisplayProps } from '@/utils/grafanaUtils';
import { formatDateTime, renderChip } from '@/utils/datagridFormatters';

const renderTraceField = (value: string, onCopy: (text: string) => void) => {
  const traceValue = value === '-' ? '' : value;
  const traceProps = createTraceDisplayProps(traceValue);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1, minWidth: 0 }}>
      <Tooltip title={traceProps.title}>
        <Typography
          variant="caption"
          aria-disabled={!traceProps.onClick}
          sx={{
            fontFamily: 'monospace',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
            ...traceProps.style,
            '&:hover': traceProps.onClick ? { color: 'primary.dark' } : undefined
          }}
          onClick={traceProps.onClick ? (e) => {
            e.stopPropagation();
            traceProps.onClick?.();
          } : undefined}
        >
          {value}
        </Typography>
      </Tooltip>
      {value !== '-' && (
        <Tooltip title="Copy Trace">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onCopy(value); }} sx={{ flexShrink: 0 }}>
            <ContentCopy fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export interface EventsColumnsProps {
  onCopy: (text: string) => void;
  onDelete: (event: Event) => void;
  canDelete: boolean;
  deleteTooltip?: string;
}

export const createEventsColumns = ({
  onCopy,
  onDelete,
  canDelete,
  deleteTooltip,
}: EventsColumnsProps): GridColDef[] => [
  {
    field: 'id',
    headerName: 'Event ID',
    width: 200,
    valueGetter: (_, row) => row.id || '-',
  },
  {
    field: 'actualDateTime',
    headerName: 'Actual Date/Time',
    flex: 1,
    minWidth: 160,
    valueGetter: (_, row) => row.actualDateTime ? formatDateTime(row.actualDateTime) : '-',
  },
  {
    field: 'domain',
    headerName: 'Domain',
    width: 120,
    renderCell: (params: any) => renderChip('domain', params.value),
  },
  {
    field: 'eventType',
    headerName: 'Event Type',
    flex: 1,
    minWidth: 150,
    valueGetter: (_, row) => row.eventType || '-',
    renderCell: (params: any) => renderChip('eventType', params.value, 'outlined', 'primary'),
  },
  {
    field: 'code',
    headerName: 'Code',
    flex: 0.8,
    minWidth: 120,
    valueGetter: (_, row) => row.code || '-',
    renderCell: (params: any) => renderChip('code', params.value, 'outlined', 'primary'),
  },
  {
    field: 'creationDateTime',
    headerName: 'Creation Date/Time',
    flex: 1,
    minWidth: 160,
    valueGetter: (_, row) => row.creationDateTime ? formatDateTime(row.creationDateTime) : '-',
  },
  {
    field: 'creationTraceparent',
    headerName: 'Creation Traceparent',
    flex: 1,
    minWidth: 100,
    valueGetter: (_, row) => row.creationTraceparent || '-',
    renderCell: (params) => renderTraceField(params.value, onCopy),
  },
  {
    field: 'reasonCode',
    headerName: 'Reason Code',
    flex: 0.8,
    minWidth: 120,
    valueGetter: (_, row) => row.reasonCode || '-',
  },
  {
    field: 'location.locationName',
    headerName: 'Location Name',
    flex: 1,
    minWidth: 150,
    valueGetter: (_, row) => row.location?.locationName || '-',
  },
  {
    field: 'location.locationType',
    headerName: 'Location Type',
    flex: 0.8,
    minWidth: 120,
    valueGetter: (_, row) => row.location?.locationType || '-',
  },
  {
    field: 'location.code',
    headerName: 'Location Code',
    flex: 0.8,
    minWidth: 120,
    valueGetter: (_, row) => row.location?.code || '-',
  },
  {
    field: 'location.address.addressLocality',
    headerName: 'Address Locality',
    flex: 1,
    minWidth: 120,
    valueGetter: (_, row) => row.location?.address?.addressLocality || '-',
  },
  {
    field: 'location.address.addressRegion',
    headerName: 'Address Region',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (_, row) => row.location?.address?.addressRegion || '-',
  },
  {
    field: 'location.address.postalCode',
    headerName: 'Postal Code',
    flex: 0.6,
    minWidth: 100,
    valueGetter: (_, row) => row.location?.address?.postalCode || '-',
  },
  {
    field: 'location.address.postOfficeBoxNumber',
    headerName: 'Post Office Box Number',
    flex: 0.6,
    minWidth: 100,
    valueGetter: (_, row) => row.location?.address?.postOfficeBoxNumber || '-',
  },
  {
    field: 'location.address.addressLines',
    headerName: 'Address Lines',
    flex: 1.2,
    minWidth: 180,
    valueGetter: (_, row) => {
      const lines = row.location?.address?.addressLines;
      return lines && lines.length > 0 ? lines.join(', ') : '-';
    },
  },
  {
    field: 'location.address.addressCountry.countryCode',
    headerName: 'Country Code',
    flex: 0.6,
    minWidth: 100,
    valueGetter: (_, row) => row.location?.address?.addressCountry?.countryCode || '-',
  },
  {
    field: 'location.address.addressCountry.countryName',
    headerName: 'Country Name',
    flex: 1,
    minWidth: 120,
    valueGetter: (_, row) => row.location?.address?.addressCountry?.countryName || '-',
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 100,
    sortable: false,
    filterable: false,
    renderCell: (params: any) => (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title={!canDelete && deleteTooltip ? deleteTooltip : 'Delete event'}>
          <span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(params.row);
              }}
              disabled={!canDelete}
              color="error"
            >
              <Delete fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    ),
  },
];

export const defaultEventsColumnVisibilityModel: Record<string, boolean> = {
  id: false,
  actualDateTime: true,
  domain: true,
  eventType: true,
  code: true,
  creationDateTime: true,
  creationTraceparent: true,
  reasonCode: false,
  actions: true,
  'location.locationName': false,
  'location.locationType': false,
  'location.code': false,
  'location.address.addressLocality': false,
  'location.address.addressRegion': false,
  'location.address.postalCode': false,
  'location.address.postOfficeBoxNumber': false,
  'location.address.addressLines': false,
  'location.address.addressCountry.countryCode': false,
  'location.address.addressCountry.countryName': false,
};
